import { Link } from "react-router-dom";
import { useReveal } from "@/lib/useReveal";
import InclusionSection from "@/components/InclusionSection";

const RIPPLE = "https://static.prod-images.emergentagent.com/jobs/7c3bd71d-f3a1-4475-868e-2031ff56ad92/images/0da293f3e2da169b65ef1706c0ba1a148d5f9fb022de9609c007727662d20643.png";

export default function About() {
  useReveal();
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="ripple-bg">
        <div className="container-app pt-24 sm:pt-32 pb-20">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="eyebrow">About CLEAN Water Drop Association</p>
              <h1 className="font-heading mt-6 text-[44px] sm:text-[64px] lg:text-[76px] leading-[0.98] tracking-[-0.02em] font-semibold text-[#0A2540]">
                Holistic, trauma-informed support
                <br />
                <span className="text-[#2E8B8B]">for people in hardship.</span>
              </h1>
            </div>
            <div className="lg:col-span-4 reveal">
              <p className="text-lg text-slate-600 leading-relaxed">
                CLEAN Water Drop Association plays a critical role in supporting vulnerable
                people facing the overlapping challenges of homelessness, alcohol abuse,
                trauma, and mental health — rather than treating these problems in isolation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RIPPLE COMMUNITY IMAGE */}
      <section className="container-app pb-24">
        <div className="relative aspect-[21/9] rounded-[2.5rem] overflow-hidden border border-slate-100 reveal">
          <img src={RIPPLE} alt="A single drop of water creating ripples" className="absolute inset-0 h-full w-full object-cover" />
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="section-y border-t border-slate-100">
        <div className="container-app grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-4 reveal">
            <p className="eyebrow">Our approach</p>
            <h2 className="font-heading mt-5 text-4xl sm:text-5xl text-[#0A2540] leading-[1.05] tracking-tight">
              Holistic, trauma-informed care.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 reveal">
            <p className="text-xl text-slate-700 leading-[1.6] font-light">
              We use a holistic, trauma-informed approach to address both immediate survival
              needs and long-term stability.
            </p>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Our work spans short-term shelter and crisis accommodation, food and nutritional
              support, laundry and personal hygiene services, mental health and psychosocial
              support, mentorship and case support, education and employment pathways, life
              skills development, and after-hours engagement.
            </p>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Many of the people we walk with — women, young people, and those leaving custody —
              face significant barriers including disrupted education, limited employment
              opportunities, unstable housing, and disconnection from positive networks.
            </p>
          </div>
        </div>
      </section>

      {/* NO WRONG DOOR — editorial minimal */}
      <section className="section-y">
        <div className="container-app">
          <div className="grid lg:grid-cols-12 gap-16 items-start reveal">
            <div className="lg:col-span-4">
              <p className="eyebrow text-[#2E8B8B]">The "No Wrong Door" approach</p>
              <h2 className="font-heading mt-5 text-4xl sm:text-5xl text-[#0A2540] leading-[1.05] tracking-tight">
                An entry point to the wider support system.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <p className="text-xl sm:text-2xl text-slate-700 leading-[1.5] font-light">
                "Perhaps the most vital role of CLEAN Water Drop Association is acting as an
                entry point to the wider social support system. When a woman walks into a
                non-profit hub just to use a washing machine or grab a sandwich, she is met
                with non-judgmental staff. That single interaction often opens the door to
                mental health care, legal support, and permanent housing."
              </p>

              <div className="mt-12 h-px bg-slate-200" />

              <div className="mt-10 grid sm:grid-cols-2 gap-x-12 gap-y-6">
                {[
                  { k: "01", v: "A non-judgmental front door" },
                  { k: "02", v: "Holistic, trauma-informed practice" },
                  { k: "03", v: "Warm referrals into wider support" },
                  { k: "04", v: "Care that follows the person" },
                ].map((p) => (
                  <div key={p.k} className="flex items-baseline gap-5">
                    <span className="font-heading text-sm text-[#2E8B8B] tracking-widest">{p.k}</span>
                    <span className="text-base text-[#0A2540]">{p.v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-y bg-[#F8FAFC]">
        <div className="container-app grid md:grid-cols-3 gap-6 lg:gap-8">
          {[
            { label: "Our mission", text: "Provide holistic, trauma-informed support that addresses both immediate survival needs and long-term stability for vulnerable women, young people, and those experiencing homelessness." },
            { label: "Our approach", text: "Hold the whole person — housing, food, hygiene, mental health, mentorship, and pathways into education and employment — through one non-judgmental front door." },
            { label: "Our values", text: "Dignity. Non-judgmental support. Holistic care. Cultural respect. Safe spaces. Long-term stability over short-term fixes." },
          ].map((v) => (
            <div key={v.label} className="rounded-3xl bg-white border border-slate-100 p-10 reveal">
              <p className="eyebrow">{v.label}</p>
              <p className="mt-5 text-[15px] text-[#0A2540] leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      <InclusionSection variant="soft" />

      <section className="container-app py-24">
        <div className="rounded-[2.5rem] bg-[#0A2540] text-white p-12 sm:p-16 grid lg:grid-cols-2 gap-10 items-end">
          <div>
            <p className="text-xs uppercase tracking-[0.26em] text-[#3AABAB]">Get involved</p>
            <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              Support the work or join the team.
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 lg:justify-end">
            <Link to="/volunteer" data-testid="about-volunteer-cta" className="btn-secondary">Volunteer</Link>
            <Link to="/donate" data-testid="about-donate-cta" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/30 px-7 py-3.5 text-sm font-medium text-white hover:bg-white hover:text-[#0A2540] transition-all">
              Donate
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
